import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import * as XLSX from 'xlsx';
import {
  FreeRangeImportPreviewRow,
  ImportDiscountFreeRangeItemStatus,
  ImportDiscountFreeRangeResult,
} from '../../../../../core/models/parking/discount-free-range.model';
import { MapperDiscountFreeRangeService } from '../../../../../core/mappers/parking/mapper-discount-free-range.service';
import { ApiDiscountFreeRangeService } from '../../../../../core/services/api/parking/api-discount-free-range.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { PssConfirmModalComponent } from '../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { FREE_RANGE_DATE_FORMATS } from '../modal-discount-free-range/modal-discount-free-range.component';

const MAX_IMPORT_PLATES = 500;
const HEADER_PATTERN = /ทะเบียน|license|plate/i;

@Component({
  selector: 'app-modal-discount-free-range-import',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssConfirmModalComponent,
    PssLoadingComponent,
  ],
  templateUrl: './modal-discount-free-range-import.component.html',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: FREE_RANGE_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
})
export class ModalDiscountFreeRangeImportComponent {
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();

  private readonly api = inject(ApiDiscountFreeRangeService);
  readonly mapper = inject(MapperDiscountFreeRangeService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  readonly maxPlates = MAX_IMPORT_PLATES;
  readonly timePresets = ['00:00', '08:00', '12:00', '18:00', '23:59'];

  step = signal<'form' | 'result'>('form');
  isImporting = signal(false);
  isReading = signal(false);
  showConfirm = false;

  startDate: Date | null = new Date();
  endDate: Date | null = new Date();
  startTime = '00:00';
  endTime = '23:59';
  remark = '';

  fileName = '';
  previewRows: FreeRangeImportPreviewRow[] = [];
  emptyRowCount = 0;

  result: ImportDiscountFreeRangeResult | null = null;
  resultFilter: 'all' | ImportDiscountFreeRangeItemStatus = 'all';

  // ---------- Range ----------

  get startAt(): Date | null {
    return this.mapper.combineDateTime(this.startDate, this.startTime);
  }

  get endAt(): Date | null {
    return this.mapper.combineDateTime(this.endDate, this.endTime);
  }

  get rangeInvalid(): boolean {
    return !!this.startAt && !!this.endAt && this.endAt <= this.startAt;
  }

  get rangePreview(): string {
    if (!this.startAt || !this.endAt) return '';
    return `${this.mapper.formatDisplayDateTime(this.startAt.toISOString())} – ${this.mapper.formatDisplayDateTime(this.endAt.toISOString())}`;
  }

  onTimeBlur(field: 'start' | 'end'): void {
    const current = field === 'start' ? this.startTime : this.endTime;
    const normalized = this.mapper.normalizeHm(current);
    if (!normalized) {
      if (current.trim()) this.toast.error('รูปแบบเวลาไม่ถูกต้อง ใช้ HH:mm เช่น 08:30');
      return;
    }
    if (field === 'start') this.startTime = normalized;
    else this.endTime = normalized;
  }

  setTime(field: 'start' | 'end', value: string): void {
    if (field === 'start') this.startTime = value;
    else this.endTime = value;
  }

  // ---------- Preview stats ----------

  get changedCount(): number {
    return this.previewRows.filter((r) => r.changed).length;
  }

  get duplicateCount(): number {
    return this.previewRows.filter((r) => r.duplicate).length;
  }

  get uniqueCount(): number {
    return this.previewRows.length - this.duplicateCount;
  }

  get overLimit(): boolean {
    return this.previewRows.length > MAX_IMPORT_PLATES;
  }

  get canSubmit(): boolean {
    return (
      this.previewRows.length > 0 &&
      !this.overLimit &&
      !!this.startAt &&
      !!this.endAt &&
      !this.rangeInvalid &&
      !this.isImporting()
    );
  }

  // ---------- File ----------

  downloadTemplate(): void {
    const sheet = XLSX.utils.aoa_to_sheet([['ทะเบียนรถ'], ['กข 1234'], ['1กก 999'], ['ขค5678']]);
    sheet['!cols'] = [{ wch: 20 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'FreeRange');
    XLSX.writeFile(book, 'FreeRange_Import_Template.xlsx');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xls', 'csv', 'txt'].includes(ext)) {
      this.toast.error('รองรับเฉพาะไฟล์ .xlsx, .xls, .csv, .txt');
      return;
    }

    this.isReading.set(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const values = ext === 'xlsx' || ext === 'xls'
          ? this.readFirstColumnFromWorkbook(XLSX.read(reader.result as ArrayBuffer, { type: 'array' }))
          : ext === 'csv'
            ? this.readFirstColumnFromWorkbook(XLSX.read(reader.result as string, { type: 'string' }))
            : String(reader.result ?? '').split(/\r?\n/);
        this.buildPreview(values);
        this.fileName = file.name;
        if (this.previewRows.length === 0) this.toast.error('ไม่พบทะเบียนรถในไฟล์');
      } catch (err) {
        console.error(err);
        this.toast.error('อ่านไฟล์ไม่สำเร็จ');
        this.clearFile();
      } finally {
        this.isReading.set(false);
      }
    };
    reader.onerror = () => {
      this.isReading.set(false);
      this.toast.error('อ่านไฟล์ไม่สำเร็จ');
    };
    if (ext === 'xlsx' || ext === 'xls') reader.readAsArrayBuffer(file);
    else reader.readAsText(file, 'utf-8');
  }

  clearFile(): void {
    this.fileName = '';
    this.previewRows = [];
    this.emptyRowCount = 0;
  }

  private readFirstColumnFromWorkbook(book: XLSX.WorkBook): string[] {
    const sheet = book.Sheets[book.SheetNames[0]];
    if (!sheet) return [];
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: false });
    return rows.map((row) => String((row as unknown[])[0] ?? ''));
  }

  /** ใช้คอลัมน์แรกเท่านั้น · ข้ามหัวตาราง · ตัดช่องว่างในทะเบียนให้ชิดกัน */
  private buildPreview(values: string[]): void {
    const rows: FreeRangeImportPreviewRow[] = [];
    const seen = new Set<string>();
    let empty = 0;
    let headerChecked = false;

    values.forEach((value, index) => {
      const raw = String(value ?? '').trim();
      if (!headerChecked && raw) {
        headerChecked = true;
        if (HEADER_PATTERN.test(raw)) return;
      }
      const plate = this.mapper.compactPlate(raw);
      if (!plate) {
        empty++;
        return;
      }
      const duplicate = seen.has(plate);
      seen.add(plate);
      rows.push({ rowNo: index + 1, raw, plate, changed: raw !== plate, duplicate });
    });

    this.previewRows = rows;
    this.emptyRowCount = empty;
  }

  // ---------- Submit ----------

  askSubmit(): void {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startAt || !this.endAt) {
      this.toast.error('กรุณาระบุวันเวลาเริ่มต้นและสิ้นสุดให้ครบ');
      return;
    }
    if (this.rangeInvalid) {
      this.toast.error('วันเวลาสิ้นสุดต้องหลังวันเวลาเริ่มต้น');
      return;
    }
    if (this.previewRows.length === 0) {
      this.toast.error('กรุณาเลือกไฟล์ทะเบียนรถ');
      return;
    }
    if (this.overLimit) {
      this.toast.error(`นำเข้าได้สูงสุด ${MAX_IMPORT_PLATES} คันต่อครั้ง`);
      return;
    }
    this.showConfirm = true;
  }

  confirmSubmit(): void {
    this.showConfirm = false;
    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์');
      return;
    }

    this.isImporting.set(true);
    this.api.importDiscountFreeRange({
      userId,
      menuId,
      siteId: this.siteId,
      startDate: this.mapper.toYmdHm(this.startAt),
      endDate: this.mapper.toYmdHm(this.endAt),
      remark: this.remark.trim() || null,
      licensePlates: this.previewRows.map((r) => r.plate),
    }).subscribe({
      next: (res) => {
        this.isImporting.set(false);
        const mapped = this.mapper.mapImportResult(res);
        if (!mapped.isSuccess && mapped.items.length === 0) {
          this.toast.error(mapped.message || 'นำเข้าไม่สำเร็จ');
          return;
        }
        this.result = mapped;
        this.resultFilter = 'all';
        this.step.set('result');
        if (mapped.successCount > 0) this.imported.emit();
        if (mapped.failedCount > 0 || !mapped.isSuccess) this.toast.error(mapped.message);
        else this.toast.success(mapped.message || 'นำเข้าสำเร็จ');
      },
      error: (err) => {
        this.isImporting.set(false);
        this.toast.error(err?.error?.message || 'นำเข้าไม่สำเร็จ');
      },
    });
  }

  // ---------- Result ----------

  get filteredResultItems() {
    const items = this.result?.items ?? [];
    return this.resultFilter === 'all' ? items : items.filter((i) => i.status === this.resultFilter);
  }

  statusLabel(status: ImportDiscountFreeRangeItemStatus): string {
    return status === 'success' ? 'สำเร็จ' : status === 'skipped' ? 'ข้าม' : 'ไม่สำเร็จ';
  }

  exportResult(): void {
    if (!this.result) return;
    const rows = this.result.items.map((i, idx) => ({
      'ลำดับ': idx + 1,
      'ทะเบียนในไฟล์': i.rawLicensePlate,
      'ทะเบียนที่ใช้': i.licensePlate,
      'สถานะ': this.statusLabel(i.status),
      'รายละเอียด': i.message,
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet['!cols'] = [{ wch: 8 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 50 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'ImportResult');
    const stamp = this.mapper.toYmd(new Date()).replace(/-/g, '');
    XLSX.writeFile(book, `FreeRange_Import_Result_${stamp}.xlsx`);
  }

  importAnother(): void {
    this.result = null;
    this.clearFile();
    this.step.set('form');
  }

  onClose(): void {
    if (this.isImporting()) return;
    this.close.emit();
  }
}
